import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Button, Tabs, Tab, IconButton, SvgIcon, Tooltip, Stack } from "@mui/material";
import { useHistory, useLocation, useParams } from "react-router";
import { useGetContentModelsQuery, useGetContentModelFieldsQuery, useGetWebViewsQuery } from "../../../../../shell/services/instance";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { formatLocalized } from "shell/i18n/dates";
import { isValid } from "date-fns";
import { InfoRounded, ApiRounded, SplitscreenRounded, HistoryRounded } from "@mui/icons-material";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import VerticalSplitRoundedIcon from "@mui/icons-material/VerticalSplitRounded";
import { modelIconMap, modelNameMap } from "../utils";
import { ModelMenu } from "./ModelMenu";
import { ModelBreadcrumbs } from "./ModelBreadcrumbs";
const getTabs = (t: (key: string) => string) => [{
  name: t("schema.tabFields"),
  value: "fields",
  icon: SplitscreenRounded
}, {
  name: t("common.apis"),
  value: "api",
  icon: ApiRounded
}, {
  name: t("schema.tabActivityLog"),
  value: "activity-log",
  icon: HistoryRounded
}, {
  name: t("schema.tabInfo"),
  value: "info",
  icon: InfoRounded
}];
type Params = {
  id: string;
};
interface Props {
  onNewFieldModalClick: (sortIndex: number | null) => void;
}
export const ModelHeader = ({
  onNewFieldModalClick
}: Props) => {
  const {
    t
  } = useTranslation();
  const TABS = getTabs(t);
  const params = useParams<Params>();
  const {
    id
  } = params;
  const {
    data: models
  } = useGetContentModelsQuery();
  const {
    data: views
  } = useGetWebViewsQuery();
  const location = useLocation();
  const history = useHistory();
  const {
    isSuccess: isFieldsLoaded
  } = useGetContentModelFieldsQuery({
    modelZUID: id
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const model = models?.find(model => model.ZUID === id);
  const view = views?.find(view => view?.contentModelZUID === model?.ZUID);
  const canCreateModel = model?.name.toLowerCase() !== "clippings";
  const updatedAt = model?.updatedAt ? new Date(model.updatedAt) : null;
  const lastUpdated = updatedAt && isValid(updatedAt) ? t("common.dateAtTime", {
    date: formatLocalized(updatedAt, "do MMMM yyyy"),
    time: formatLocalized(updatedAt, "h:mm a")
  }) : "";
  return <>
      <Box sx={{
      backgroundColor: "background.paper",
      borderBottom: theme => `2px solid ${theme.palette.border}`
    }} data-sentry-element="Box" data-sentry-source-file="ModelHeader.tsx">
        <Stack px={4} pt={4} pb={1} direction="row" justifyContent="space-between" alignItems="flex-start" data-sentry-element="Stack" data-sentry-source-file="ModelHeader.tsx">
          <Stack gap={0.25} justifyContent="space-between" data-sentry-element="Stack" data-sentry-source-file="ModelHeader.tsx">
            <ModelBreadcrumbs modelZUID={model?.ZUID} data-sentry-element="ModelBreadcrumbs" data-sentry-source-file="ModelHeader.tsx" />
            <Box display="flex" alignItems="center" gap={0.5} data-sentry-element="Box" data-sentry-source-file="ModelHeader.tsx">
              <Typography variant="h3" fontWeight={700} data-sentry-element="Typography" data-sentry-source-file="ModelHeader.tsx">
                {model?.label}
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" gap={0.25} data-sentry-element="Stack" data-sentry-source-file="ModelHeader.tsx">
              <SvgIcon component={modelIconMap[model?.type as keyof typeof modelIconMap]} sx={{
              color: "text.secondary",
              fontSize: "18px"
            }} data-sentry-element="SvgIcon" data-sentry-source-file="ModelHeader.tsx" />
              <Typography variant="body3" color="text.secondary" whiteSpace="pre" data-sentry-element="Typography" data-sentry-source-file="ModelHeader.tsx">{`${t("schema.modelTypeLabel", {
                modelType: t(modelNameMap[model?.type])
              })}  •  `}</Typography>
              <Typography variant="body3" color="text.secondary" data-sentry-element="Typography" data-sentry-source-file="ModelHeader.tsx">
                {t("schema.lastUpdated", {
                date: lastUpdated
              })}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" gap={1} alignItems="center" data-sentry-element="Stack" data-sentry-source-file="ModelHeader.tsx">
            <IconButton size="small" onClick={event => setAnchorEl(event.currentTarget)} sx={{
            height: "fit-content"
          }} data-cy="model-header-menu" data-sentry-element="IconButton" data-sentry-source-file="ModelHeader.tsx">
              <MoreHorizRoundedIcon fontSize="small" data-sentry-element="MoreHorizRoundedIcon" data-sentry-source-file="ModelHeader.tsx" />
            </IconButton>
            <ModelMenu anchorEl={anchorEl} modelZUID={id} onClose={() => setAnchorEl(null)} data-sentry-element="ModelMenu" data-sentry-source-file="ModelHeader.tsx" />
            {view && <Button size="small" variant="outlined" color="inherit" startIcon={<CodeRoundedIcon color="action" />} onClick={() => history.push(`/code/file/views/${view?.ZUID}`)}>
                {t("schema.editInCode")}
              </Button>}
            {/* {canCreateModel && (
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={<PostAddRoundedIcon color="action" />}
                  onClick={() => history.push(`/content/${model?.ZUID}/new`)}
                >
                  <Box
                    sx={{
                      maxWidth: "220px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Create {model?.label}
                  </Box>
                </Button>
              )} */}
            <Button size="small" variant="outlined" color="inherit" startIcon={<VerticalSplitRoundedIcon color="action" />} onClick={() => {
            if (model?.type === "block") {
              history.push(`/blocks/${model?.ZUID}`);
            } else {
              history.push(`/content/${model?.ZUID}`);
            }
          }} data-sentry-element="Button" data-sentry-source-file="ModelHeader.tsx">
              {model?.type === "block" ? t("schema.viewVariants") : t("schema.viewContent")}
            </Button>
            <Button size="small" variant="contained" startIcon={<AddRoundedIcon />} onClick={() => onNewFieldModalClick(null)} disabled={!isFieldsLoaded} data-cy="AddFieldBtn" data-sentry-element="Button" data-sentry-source-file="ModelHeader.tsx">
              {t("schema.addField")}
            </Button>
          </Stack>
        </Stack>
        <Tabs sx={{
        position: "relative",
        top: "2px",
        px: 4
      }} value={location.pathname.split("/")[3] || TABS[0].value} onChange={(event, value) => history.push(`/schema/${model?.ZUID}/${value}`)} data-sentry-element="Tabs" data-sentry-source-file="ModelHeader.tsx">
          {TABS?.map(tab => <Tab key={tab.value} icon={<SvgIcon component={tab.icon} fontSize="small" />} iconPosition="start" label={tab.name} value={tab.value} disableRipple />)}
        </Tabs>
      </Box>
    </>;
};